// src/components/sales/SalesInvoiceDetail.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Divider,
  Paper,
  Stack,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Link,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Send,
  PictureAsPdf,
  Person,
  CalendarToday,
  Payment,
  Business,
  Sell,
  AttachFile,
  Description,
  OpenInNew,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import { sendInvoiceEmail } from "../../services/emailService";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import { PAYMENT_METHOD_LABELS, SALES_INVOICE_STATUS_LABELS } from "../../utils/models/salesInvoiceModel";

const SalesInvoiceDetail = ({ invoiceId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { document: invoice, loading } = useDocument(
    "salesInvoices",
    invoiceId,
  );
  const { data: projects } = useCollection("projects");

  if (loading) return <LoadingSpinner />;
  if (!invoice) return <EmptyState title="Factura de venta no encontrada" />;

  const statusColors = {
    unpaid: "warning",
    paid: "success",
  };

  // Convertir valores numéricos de forma segura
  const baseAmount = parseFloat(invoice.baseAmount) || 0;
  const taxAmount = parseFloat(invoice.taxAmount) || 0;
  const total = parseFloat(invoice.total) || 0;
  const taxRate = parseFloat(invoice.taxRate) || 0;

  // Formatear números de forma segura
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return num.toFixed(2);
  };

  const handleSend = () => {
    if (invoice.clientEmail)
      sendInvoiceEmail(
        invoice.clientEmail,
        invoice.invoiceNumber,
        invoice.clientName,
      );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
        overflow: "hidden",
      }}
    >
      {/* CABECERA */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 2 },
          mb: { xs: 3, sm: 4 },
          flexWrap: "wrap",
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          size="small"
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
            mt: 0.5,
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              wordBreak: "break-word",
            }}
          >
            Factura de Venta {invoice.invoiceNumber || "BORRADOR"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Chip
              icon={<Sell sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              label="Venta"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
            <Chip
              label={SALES_INVOICE_STATUS_LABELS[invoice.status] || invoice.status}
              color={statusColors[invoice.status] || "default"}
              size="small"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
            {invoice.uploadedToDrive && (
              <Chip
                label="Drive"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            )}
          </Box>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            startIcon={<Send />}
            variant="outlined"
            onClick={handleSend}
            size="small"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              height: { xs: 36, sm: 44 },
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
          >
            Enviar
          </Button>
          <Button
            startIcon={<PictureAsPdf />}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              height: { xs: 36, sm: 44 },
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
          >
            PDF
          </Button>
          <Button
            startIcon={<Edit />}
            variant="contained"
            onClick={() => onEdit && onEdit(invoice)}
            size="small"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              height: { xs: 36, sm: 44 },
              boxShadow: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
          >
            Editar
          </Button>
        </Stack>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          width: "100%",
          flexDirection: { xs: "column", md: "row-reverse" },
        }}
      >
        {/* IMPORTES */}
        <Box
          sx={{
            flex: { md: "2 1 540px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "350px" },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Importes
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  background: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    Base imponible
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {formatCurrency(baseAmount)}€
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    IVA ({taxRate}%)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {formatCurrency(taxAmount)}€
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}
                  >
                    Total
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color={theme.palette.primary.main}
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    {formatCurrency(total)}€
                  </Typography>
                </Box>
              </Box>
              
              {/* PROYECTOS ASOCIADOS */}
              {invoice.projects && invoice.projects.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      mt: 2,
                      mb: 1,
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  >
                    Proyectos asociados
                  </Typography>
                  {invoice.projects.map((projectAssignment) => {
                    const project = projects?.find(
                      (p) => p.id === projectAssignment.projectId,
                    );
                    const projectAmount = parseFloat(projectAssignment.amount) || 0;
                    return (
                      <Box
                        key={projectAssignment.projectId}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                          p: 1,
                          borderRadius: "8px",
                          bgcolor: theme.palette.background.default,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                        >
                          {project?.title || project?.name || "Proyecto"}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                        >
                          {projectAmount.toFixed(2)}€
                        </Typography>
                      </Box>
                    );
                  })}
                </>
              )}

              {/* NUEVO: ARCHIVOS ADJUNTOS */}
              {invoice.files && invoice.files.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      mb: 2,
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    }}
                  >
                    <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    Archivos adjuntos
                  </Typography>
                  <List dense disablePadding>
                    {invoice.files.map((file, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Description sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              wordBreak: 'break-all',
                              '&:hover': {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          >
                            {file.name || file.url}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </ListItemText>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>

          {/* NOTAS */}
          {invoice.notes && (
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                background: theme.palette.background.paper,
                mb: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    mb: 2,
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  Notas
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" }, whiteSpace: "pre-wrap" }}
                >
                  {invoice.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* INFORMACIÓN */}
        <Box
          sx={{
            flex: { md: "1 1 340px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "300px" },
          }}
        >
          {/* EMISOR (EPC) */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Emisor (EPC)
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Business
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: theme.palette.primary.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={invoice.issuerName}
                    secondary={invoice.issuerTaxId}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 600,
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  />
                </ListItem>
                {invoice.issuerAddress && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Dirección"
                      secondary={invoice.issuerAddress}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* CLIENTE */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Cliente
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Person
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: theme.palette.primary.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={invoice.clientName}
                    secondary={invoice.clientEmail || invoice.clientTaxId}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 600,
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      wordBreak: "break-all",
                    }}
                  />
                </ListItem>
                {invoice.clientAddress && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Dirección"
                      secondary={invoice.clientAddress}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* FECHAS */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Fechas
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CalendarToday
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: theme.palette.primary.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Emisión"
                    secondary={invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() : "-"}
                    primaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CalendarToday
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: theme.palette.warning.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Vencimiento"
                    secondary={
                      invoice.dueDate
                        ? new Date(invoice.dueDate).toLocaleDateString()
                        : "-"
                    }
                    primaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  />
                </ListItem>
                {invoice.paidAt && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CalendarToday
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pagada"
                      secondary={new Date(invoice.paidAt).toLocaleDateString()}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* COBRO */}
          {invoice.paymentMethod && (
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                background: theme.palette.background.paper,
                mb: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    mb: 2,
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  Cobro
                </Typography>
                <List dense disablePadding>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Payment
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Método"
                      secondary={
                        PAYMENT_METHOD_LABELS[invoice.paymentMethod] ||
                        invoice.paymentMethod
                      }
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                  {invoice.iban && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary="IBAN"
                        secondary={invoice.iban}
                        primaryTypographyProps={{
                          variant: "caption",
                          color: "text.secondary",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          fontSize: { xs: "0.8rem", sm: "0.85rem" },
                          wordBreak: "break-all",
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default SalesInvoiceDetail;